/**
 * Axis Communications non-camera deep-scan catalog.
 *
 * Scope:
 *  - T85xx PoE+ network switches (T8504-E outdoor, T8504-R industrial,
 *    T8508, T8516, T8524)
 *  - A1xxx / A1810-B network door controllers
 *  - A8xxx / I8xxx network video door stations (intercoms)
 *  - S-series Camera Station recording servers (S1148, S1216 Mk II,
 *    S1224, S1228, S1232 Rack/Tower, S1264, S1296)
 *
 * Out of scope (no matching EquipmentCategory in this app):
 *  - C-series network speakers / network audio bridges (C1110-E,
 *    C1310-E, C1410, C1610-VE, C8033) — no 'speaker' category.
 *  - Encoder products (M7016, P7224-V, Q7920) — no 'video-encoder'
 *    category. The unit converts analog cameras to IP; not a recorder.
 *  - T8531 / T8541 mains-to-PoE adapters — not a network switch.
 *  - SKUs that don't exist on axis.com (A1611, A1612, A1814-LE).
 *
 * All specs verified against axis.com on 2026-05-14. Each entry cites
 * the source datasheet URL above it. Fields not stated on the datasheet
 * are omitted rather than guessed.
 *
 * `verified: true` — every populated field read directly from the
 *   official Axis datasheet PDF or product page.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const AXIS_DEEP: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // T85 series — PoE+ network switches
  // ─────────────────────────────────────────────────────────────

  // https://www.axis.com/files/datasheet/ds_t8508_network_t10113965_en_2012.pdf
  // (datasheet T10113965, read 2026-05-14)
  {
    id: 'axis-t8508',
    manufacturer: 'Axis Communications',
    model: 'T8508 PoE+',
    category: 'poe-switch',
    tagline: '8-port managed Gigabit PoE+, 130 W budget, 2 SFP combo uplinks — fanless',
    verified: true,
    datasheetUrl:
      'https://www.axis.com/files/datasheet/ds_t8508_network_t10113965_en_2012.pdf',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 130,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× RJ45/SFP combo (Gigabit)',
      switchingCapacityGbps: 20,
      management: 'L2-managed',
      formFactor: 'desktop',
      fanless: true,
      operatingTempC: '0 to 50',
      surgeProtectionKV: 6,
    },
  },

  // https://www.axis.com/dam/public/59/78/1e/datasheet-axis-t8516-poe+-network-switch-en-US-281597.pdf
  // (datasheet T10076838, read 2026-05-14)
  {
    id: 'axis-t8516',
    manufacturer: 'Axis Communications',
    model: 'T8516 PoE+',
    category: 'poe-switch',
    tagline: '16-port managed Gigabit PoE+, 240 W budget, 2 SFP combo uplinks',
    verified: true,
    datasheetUrl:
      'https://www.axis.com/dam/public/59/78/1e/datasheet-axis-t8516-poe+-network-switch-en-US-281597.pdf',
    specs: {
      totalPorts: 18,
      poePorts: 16,
      poeBudgetW: 240,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× RJ45/SFP combo (Gigabit)',
      switchingCapacityGbps: 36,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
      operatingTempC: '0 to 50',
      surgeProtectionKV: 6,
    },
  },

  // https://www.axis.com/dam/public/95/61/d4/datasheet-axis-t8524-poe+-network-switch-en-US-300463.pdf
  // (datasheet T10113964, read 2026-05-14)
  {
    id: 'axis-t8524',
    manufacturer: 'Axis Communications',
    model: 'T8524 PoE+',
    category: 'poe-switch',
    tagline: '24-port managed Gigabit PoE+, 370 W budget, 2 SFP combo uplinks',
    verified: true,
    datasheetUrl:
      'https://www.axis.com/dam/public/95/61/d4/datasheet-axis-t8524-poe+-network-switch-en-US-300463.pdf',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× RJ45/SFP combo (Gigabit)',
      switchingCapacityGbps: 52,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
      operatingTempC: '0 to 50',
      surgeProtectionKV: 6,
    },
  },

  // https://www.axis.com/dam/public/32/2f/b6/datasheet-axis-t8504-e-outdoor-poe-switch-en-US-377057.pdf
  // (datasheet T10114169, read 2026-05-14)
  // 60 W per port is "AXIS High PoE" 4-pair power (not IEEE 802.3bt);
  // ports 1-2 deliver 60 W, ports 3-4 deliver 30 W (802.3at).
  {
    id: 'axis-t8504-e',
    manufacturer: 'Axis Communications',
    model: 'T8504-E Outdoor',
    category: 'poe-switch',
    tagline: '4-port outdoor PoE switch, 150 W budget, 60 W per port — IP67, fiber SFP',
    verified: true,
    datasheetUrl:
      'https://www.axis.com/dam/public/32/2f/b6/datasheet-axis-t8504-e-outdoor-poe-switch-en-US-377057.pdf',
    specs: {
      totalPorts: 5,
      poePorts: 4,
      poeBudgetW: 150,
      poeStandard: '802.3at',
      poePerPortW: 60,
      uplinks: '1× SFP',
      switchingCapacityGbps: 10,
      management: 'L2-managed',
      formFactor: 'industrial-DIN',
      fanless: true,
      operatingTempC: '-40 to 50 (-40 to 60 at 90 W)',
      surgeProtectionKV: 6,
    },
  },

  // https://www.axis.com/dam/public/89/81/ca/datasheet-axis-t8504-r-industrial-poe-switch-en-US-308120.pdf
  // (datasheet T10129544, read 2026-05-14)
  // High PoE 60 W per port (NEMA TS-2 industrial); dual DC power inputs.
  {
    id: 'axis-t8504-r',
    manufacturer: 'Axis Communications',
    model: 'T8504-R Industrial',
    category: 'poe-switch',
    tagline: '4-port industrial PoE switch, 240 W budget, 60 W per port — -40 to 75 °C, Rapid Ring',
    verified: true,
    datasheetUrl:
      'https://www.axis.com/dam/public/89/81/ca/datasheet-axis-t8504-r-industrial-poe-switch-en-US-308120.pdf',
    specs: {
      totalPorts: 8,
      poePorts: 4,
      poeBudgetW: 240,
      poeStandard: '802.3at',
      poePerPortW: 60,
      uplinks: '2× RJ45 + 2× SFP',
      switchingCapacityGbps: 16,
      management: 'L2-managed',
      formFactor: 'industrial-DIN',
      fanless: true,
      operatingTempC: '-40 to 75',
      surgeProtectionKV: 6,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // A-series network door controllers
  // ─────────────────────────────────────────────────────────────

  // https://www.axis.com/dam/public/b8/91/e0/datasheet-axis-a1001-network-door-controller-en-US-509289.pdf
  // (datasheet T10091714, read 2026-05-14)
  {
    id: 'axis-a1001',
    manufacturer: 'Axis Communications',
    model: 'A1001 Network Door Controller',
    category: 'door-controller',
    tagline: 'Open IP door controller, 1-2 doors, 2 readers (Wiegand/RS485 OSDP), PoE',
    verified: true,
    datasheetUrl:
      'https://www.axis.com/dam/public/b8/91/e0/datasheet-axis-a1001-network-door-controller-en-US-509289.pdf',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
      maxUsersStandalone: 400, // AXIS Entry Manager onboard
    },
  },

  // https://www.axis.com/dam/public/69/69/1e/datasheet-axis-a1210-network-door-controller-en-US-509586.pdf
  // (read 2026-05-14)
  {
    id: 'axis-a1210',
    manufacturer: 'Axis Communications',
    model: 'A1210 Network Door Controller',
    category: 'door-controller',
    tagline: 'Compact edge-based 1-door controller, 2 OSDP readers, PoE — 250 k credentials',
    verified: true,
    datasheetUrl:
      'https://www.axis.com/dam/public/69/69/1e/datasheet-axis-a1210-network-door-controller-en-US-509586.pdf',
    specs: {
      doors: 1,
      hostBus: 'tcp-ip',
      maxUsersStandalone: 250000,
    },
  },

  // https://www.axis.com/dam/public/1a/7e/1e/datasheet-axis-a1210-b-network-door-controller-en-US-404003.pdf
  // (read 2026-05-14) — barebone PCB variant for OEM cabinets.
  {
    id: 'axis-a1210-b',
    manufacturer: 'Axis Communications',
    model: 'A1210-B Network Door Controller',
    category: 'door-controller',
    tagline: 'Barebone edge 1-door controller, 2 OSDP readers, PoE — for OEM enclosures',
    verified: true,
    datasheetUrl:
      'https://www.axis.com/dam/public/1a/7e/1e/datasheet-axis-a1210-b-network-door-controller-en-US-404003.pdf',
    specs: {
      doors: 1,
      hostBus: 'tcp-ip',
      maxUsersStandalone: 250000,
    },
  },

  // https://www.axis.com/dam/public/33/cb/20/datasheet-axis-a1601-network-door-controller-en-US-374271.pdf
  // (datasheet T10125109, read 2026-05-14)
  {
    id: 'axis-a1601',
    manufacturer: 'Axis Communications',
    model: 'A1601 Network Door Controller',
    category: 'door-controller',
    tagline: 'Mid-size/enterprise door controller, 1-2 doors, 2 readers OSDP, PoE+',
    verified: true,
    datasheetUrl:
      'https://www.axis.com/dam/public/33/cb/20/datasheet-axis-a1601-network-door-controller-en-US-374271.pdf',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
      maxUsersStandalone: 70000,
    },
  },

  // https://www.axis.com/dam/public/3e/cf/59/datasheet-axis-a1610-network-door-controller-230207-en-US-396886.pdf
  // (datasheet T10184889, read 2026-05-14)
  {
    id: 'axis-a1610',
    manufacturer: 'Axis Communications',
    model: 'A1610 Network Door Controller',
    category: 'door-controller',
    tagline: 'Edge 2-door controller, 4 OSDP readers, PoE+ — 250 k credentials, plenum rated',
    verified: true,
    datasheetUrl:
      'https://www.axis.com/dam/public/3e/cf/59/datasheet-axis-a1610-network-door-controller-230207-en-US-396886.pdf',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
      maxUsersStandalone: 250000,
    },
  },

  // https://www.axis.com/products/axis-a1610-b
  // (read 2026-05-14) — barebone variant of A1610 for OEM cabinets.
  {
    id: 'axis-a1610-b',
    manufacturer: 'Axis Communications',
    model: 'A1610-B Network Door Controller',
    category: 'door-controller',
    tagline: 'Barebone edge 2-door controller, 4 OSDP readers, PoE+ — for OEM enclosures',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-a1610-b',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
      maxUsersStandalone: 250000,
    },
  },

  // https://www.axis.com/products/axis-a1710-b
  // (read 2026-05-14) — note: doors:1|2 type is too narrow; this 4-door
  // controller is recorded as `doors: 2` (closest enum value) and the
  // larger capacity surfaces via the tagline. UI consumers should treat
  // tagline as authoritative on doors-per-unit > 2.
  {
    id: 'axis-a1710-b',
    manufacturer: 'Axis Communications',
    model: 'A1710-B Network Door Controller',
    category: 'door-controller',
    tagline: 'Compact 4-door controller, 8 OSDP readers, PoE+ — 250 k credentials, plenum rated',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-a1710-b',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
      maxUsersStandalone: 250000,
    },
  },

  // https://www.axis.com/products/axis-a1810-b
  // (read 2026-05-14) — 8-door multi-door barebone controller. Same
  // doors-enum caveat as A1710-B above.
  {
    id: 'axis-a1810-b',
    manufacturer: 'Axis Communications',
    model: 'A1810-B Network Door Controller',
    category: 'door-controller',
    tagline: '8-door multi-door controller, 16 OSDP readers, PoE+ — 250 k credentials, 17 relays',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-a1810-b',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
      maxUsersStandalone: 250000,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // A8xxx / I8xxx network video door stations (intercoms)
  // ─────────────────────────────────────────────────────────────

  // https://www.axis.com/dam/public/b2/6b/e3/datasheet-axis-a8004-ve-en-US-293113.pdf
  // (datasheet T10100743, read 2026-05-14)
  {
    id: 'axis-a8004-ve',
    manufacturer: 'Axis Communications',
    model: 'A8004-VE Network Video Door Station',
    category: 'intercom',
    tagline: '1.3 MP IP video door station, 1 call button, IP66 IK08 — SIP, PoE+',
    verified: true,
    datasheetUrl:
      'https://www.axis.com/dam/public/b2/6b/e3/datasheet-axis-a8004-ve-en-US-293113.pdf',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 1.3,
      callButtons: 1,
      ipRating: 'IP66 / IK08',
      poe: true,
    },
  },

  // https://www.axis.com/dam/public/25/0b/ad/datasheet-axis-a8105-e-en-US-359690.pdf
  // (datasheet T10069545, read 2026-05-14)
  {
    id: 'axis-a8105-e',
    manufacturer: 'Axis Communications',
    model: 'A8105-E Network Video Door Station',
    category: 'intercom',
    tagline: '2 MP IP video door station, 1 call button, 180° FoV — SIP, PoE',
    verified: true,
    datasheetUrl:
      'https://www.axis.com/dam/public/25/0b/ad/datasheet-axis-a8105-e-en-US-359690.pdf',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2.3, // 1920×1200
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
    },
  },

  // https://www.axis.com/dam/public/84/94/55/datasheet-axis-a8207-ve-network-video-door-station-en-US-353724.pdf
  // (read 2026-05-14)
  {
    id: 'axis-a8207-ve',
    manufacturer: 'Axis Communications',
    model: 'A8207-VE Network Video Door Station',
    category: 'intercom',
    tagline: '6 MP IP door station with MIFARE reader + keypad — IP66 IK08, SIP, PoE+',
    verified: true,
    datasheetUrl:
      'https://www.axis.com/dam/public/84/94/55/datasheet-axis-a8207-ve-network-video-door-station-en-US-353724.pdf',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 6,
      ipRating: 'IP66 / IK08',
      poe: true,
      accessControl: ['mifare', 'keypad'],
    },
  },

  // https://www.axis.com/products/axis-a8207-ve-mk-ii
  // (read 2026-05-14) — Mk II refresh of the A8207-VE with same 6 MP +
  // MIFARE + keypad combination.
  {
    id: 'axis-a8207-ve-mk-ii',
    manufacturer: 'Axis Communications',
    model: 'A8207-VE Mk II Network Video Door Station',
    category: 'intercom',
    tagline: '6 MP IP door station Mk II with MIFARE + keypad + PIR — IP66 IK08, SIP, PoE+',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-a8207-ve-mk-ii',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 6,
      ipRating: 'IP66 / IK08',
      poe: true,
      accessControl: ['mifare', 'keypad'],
    },
  },

  // https://www.axis.com/products/axis-i8116-e
  // (read 2026-05-14) — replacement for A8105-E, mullion-form factor.
  {
    id: 'axis-i8116-e',
    manufacturer: 'Axis Communications',
    model: 'I8116-E Network Video Intercom',
    category: 'intercom',
    tagline: '5 MP mullion-form IP intercom, 48 mm wide — IP65 IK07, SIP, PoE',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-i8116-e',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 5,
      callButtons: 1,
      ipRating: 'IP65 / IK07',
      poe: true,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // S-series Camera Station recording servers
  // ─────────────────────────────────────────────────────────────

  // https://www.axis.com/products/axis-s1148
  // (read 2026-05-14) — S11 rack recorder, 64 validated channels.
  {
    id: 'axis-s1148',
    manufacturer: 'Axis Communications',
    model: 'Camera Station S1148',
    category: 'nvr-recorder',
    tagline: '64-channel 2U rack recording server, 12 HDD bays — up to 144 TB, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-s1148',
    specs: {
      channels: 64,
      inputBandwidthMbps: 512,
      hddBays: 12,
      codecs: ['H.264', 'H.265'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
    },
  },

  // https://www.axis.com/products/axis-s1216-tower-mk-ii
  // (read 2026-05-14) — small-site tower recorder.
  {
    id: 'axis-s1216-tower-mk-ii',
    manufacturer: 'Axis Communications',
    model: 'Camera Station S1216 Tower Mk II',
    category: 'nvr-recorder',
    tagline: '32-channel tower recording server, 2 HDD bays — RAID 0/1, 16 ACS Pro licenses',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-s1216-tower-mk-ii',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      hddBays: 2,
      codecs: ['H.264', 'H.265'],
      raidLevels: ['0', '1'],
      onvif: true,
      formFactor: 'desktop',
    },
  },

  // https://www.axis.com/products/axis-s1224-rack
  // (read 2026-05-14) — 1U rack, 32 channels.
  {
    id: 'axis-s1224-rack',
    manufacturer: 'Axis Communications',
    model: 'Camera Station S1224 Rack',
    category: 'nvr-recorder',
    tagline: '32-channel 1U rack recording server, 2 HDD bays — RAID 0/1/10, 24 ACS Pro licenses',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-s1224-rack',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      hddBays: 2,
      codecs: ['H.264', 'H.265'],
      raidLevels: ['0', '1', '10'],
      onvif: true,
      formFactor: '1U',
    },
  },

  // https://www.axis.com/products/axis-s1228-rack
  // (read 2026-05-14) — AI-optimized 1U rack, GPU acceleration.
  {
    id: 'axis-s1228-rack',
    manufacturer: 'Axis Communications',
    model: 'Camera Station S1228 Rack AI',
    category: 'nvr-recorder',
    tagline: 'AI-optimized 32-channel 1U rack recorder, GPU acceleration — RAID 0/1/10',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-s1228-rack',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      hddBays: 2,
      codecs: ['H.264', 'H.265'],
      raidLevels: ['0', '1', '10'],
      onvif: true,
      formFactor: '1U',
    },
  },

  // https://www.axis.com/products/axis-s1232-rack
  // (read 2026-05-14) — 1U rack, 64 channels, 500 Mbps.
  {
    id: 'axis-s1232-rack',
    manufacturer: 'Axis Communications',
    model: 'Camera Station S1232 Rack',
    category: 'nvr-recorder',
    tagline: '64-channel 1U rack recording server, 4 HDD bays — up to 32 TB, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-s1232-rack',
    specs: {
      channels: 64,
      inputBandwidthMbps: 500,
      hddBays: 4,
      codecs: ['H.264', 'H.265'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '1U',
    },
  },

  // https://www.axis.com/products/axis-s1232-tower
  // (read 2026-05-14) — tower form of S1232.
  {
    id: 'axis-s1232-tower',
    manufacturer: 'Axis Communications',
    model: 'Camera Station S1232 Tower',
    category: 'nvr-recorder',
    tagline: '64-channel tower recording server, 8 HDD bays — RAID 0/1/5/6/10, 32 ACS Pro licenses',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-s1232-tower',
    specs: {
      channels: 64,
      inputBandwidthMbps: 500,
      hddBays: 8,
      codecs: ['H.264', 'H.265'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: 'desktop',
    },
  },

  // https://www.axis.com/products/axis-s1264-rack
  // (read 2026-05-14) — 2U rack, 96 channels, 850 Mbps.
  {
    id: 'axis-s1264-rack',
    manufacturer: 'Axis Communications',
    model: 'Camera Station S1264 Rack',
    category: 'nvr-recorder',
    tagline: '96-channel 2U rack recording server, 12 HDD bays — up to 144 TB, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-s1264-rack',
    specs: {
      channels: 96,
      inputBandwidthMbps: 850,
      hddBays: 12,
      codecs: ['H.264', 'H.265'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
    },
  },

  // https://help.axis.com/en-us/axis-s1296-rack
  // (read 2026-05-14) — 2U rack, 150 channels, 1500 Mbps.
  {
    id: 'axis-s1296-rack',
    manufacturer: 'Axis Communications',
    model: 'Camera Station S1296 Rack',
    category: 'nvr-recorder',
    tagline: '150-channel 2U rack recording server, 12 HDD bays — up to 192 TB, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-s1296-rack',
    specs: {
      channels: 150,
      inputBandwidthMbps: 1500,
      hddBays: 12,
      codecs: ['H.264', 'H.265'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
    },
  },
];

// ─────────────────────────────────────────────────────────────
// Out-of-scope SKUs noted for future catalog expansion:
//
// C-series network speakers (no 'speaker' category yet):
//   - C1110-E Network Cabinet Speaker, IP66/IK08, PoE
//     https://www.axis.com/products/axis-c1110-e
//   - C1310-E Network Horn Speaker, IP66/IK10, PoE+ — outdoor area
//     https://www.axis.com/products/axis-c1310-e
//   - C1410 Network Mini Speaker, indoor ceiling, PoE
//     https://www.axis.com/products/axis-c1410
//   - C1610-VE Network Sound Projector, directional, PoE+
//     https://www.axis.com/products/axis-c1610-ve
//   - C8033 Network Audio Bridge — bridges analog audio system to IP
//     https://www.axis.com/products/axis-c8033
//
// Encoder products (no 'video-encoder' category yet):
//   - M7016 Video Encoder, 16-channel rack
//     https://www.axis.com/products/axis-m7016
//   - P7224 Video Encoder Blade (for AXIS T8646 rack chassis)
//     https://www.axis.com/products/axis-p7224
//   - Q7920 Video Encoder Chassis, holds Q7411/Q7414 blades
//     https://www.axis.com/products/axis-q7920
// ─────────────────────────────────────────────────────────────
